import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { makeTr } from "@/i18n/tr";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/useStore";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Crown, 
  Gamepad2, 
  Trophy, 
  Wallet, 
  User, 
  Medal,
  LogOut,
  Globe,
  Menu,
  X,
  Users,
  Settings,
  Bell,
  Shield,
  Gift,
  History
} from "lucide-react";
import { useState, useEffect } from "react";
import { useNotifications } from "@/hooks/useNotifications";

export const Layout = () => {
  const { t, i18n } = useTranslation();
  const tr = makeTr(i18n.language);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, setLanguage, language } = useStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Initialize real-time notifications
  const { connected, notifications } = useNotifications();

  const navItems = [
    { path: "/lobby", label: t("nav.lobby"), icon: Gamepad2 },
    { path: "/tournaments", label: t("nav.tournaments"), icon: Trophy },
    { path: "/game-history", label: t("nav.history") || "History", icon: History },
    { path: "/friends", label: t("friends.title"), icon: Users },
    { path: "/referrals", label: t("nav.referrals") || "Referrals", icon: Gift },
    { path: "/wallet", label: t("nav.wallet"), icon: Wallet },
    { path: "/leaderboard", label: t("nav.leaderboard"), icon: Medal },
    { path: "/profile", label: t("nav.profile"), icon: User },
    ...(user?.is_admin ? [{ path: "/admin", label: "Admin", icon: Shield }] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link to="/lobby" className="flex items-center gap-2 group">
              <Crown className="h-8 w-8 text-primary transition-transform group-hover:scale-110" />
              <span className="text-xl font-black tracking-tight hidden sm:inline">
                TEA<span className="text-primary">CHESS</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.slice(1)}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors
                    ${isActive(item.path) 
                      ? "bg-primary/10 text-primary" 
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Right side actions */}
            <div className="flex items-center gap-3">
              {/* Balance display - clickable link to wallet */}
              {user && (
                <Link 
                  to="/wallet"
                  className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary/10 border border-primary/20 hover:bg-primary/20 transition-colors cursor-pointer"
                  data-testid="header-wallet-btn"
                >
                  <Wallet className="h-4 w-4 text-primary" />
                  <span className="font-accent text-sm font-bold text-primary">
                    {user.balance?.toFixed(2) || "0.00"} Points
                  </span>
                </Link>
              )}

              {/* Language selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9">
                    <Globe className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem 
                    onClick={() => handleLanguageChange("en")}
                    className={language === "en" ? "bg-primary/10" : ""}
                  >
                    English
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => handleLanguageChange("ru")}
                    className={language === "ru" ? "bg-primary/10" : ""}
                    data-testid="lang-ru"
                  >
                    Русский
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => handleLanguageChange("zh")}
                    className={language === "zh" ? "bg-primary/10" : ""}
                    data-testid="lang-zh"
                  >
                    中文
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2 hidden sm:flex">
                    <div className="h-7 w-7 rounded-full bg-primary/20 flex items-center justify-center">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <span className="font-medium">{user?.username || "User"}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate("/profile")}>
                    <User className="h-4 w-4 mr-2" />
                    {t("nav.profile")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/wallet")}>
                    <Wallet className="h-4 w-4 mr-2" />
                    {t("nav.wallet")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                    <LogOut className="h-4 w-4 mr-2" />
                    {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile menu button */}
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border/50 bg-background">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors
                    ${isActive(item.path) 
                      ? "bg-primary/10 text-primary" 
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Link>
              ))}
              <div className="border-t border-border/50 mt-2 pt-4">
                <Link 
                  to="/wallet"
                  className="flex items-center justify-between px-4 py-2 hover:bg-muted rounded-md transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-wallet-btn"
                >
                  <span className="text-sm text-muted-foreground">Balance</span>
                  <span className="font-accent font-bold text-primary">
                    ${user?.balance?.toFixed(2) || "0.00"}
                  </span>
                </Link>
              </div>
              <Button 
                variant="ghost" 
                className="justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={handleLogout}
              >
                <LogOut className="h-5 w-5 mr-3" />
                {t("nav.logout")}
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        <Outlet />
      </main>
      
      {/* Footer with Terms */}
      <footer className="border-t border-border/40 py-4 mt-auto">
        <div className="container mx-auto px-4 flex justify-center items-center gap-4 text-xs text-muted-foreground">
          <Link to="/terms" className="hover:text-primary transition-colors">
            {tr("Пользовательское соглашение", "Terms of Service")}
          </Link>
          <span>•</span>
          <span>© 2026 TeaChess</span>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
